import React, { useEffect, useState, useCallback, startTransition, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Autocomplete,
  Pagination,
  IconButton,
  MenuItem,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery } from '@tanstack/react-query';
import { useClinic } from '../../components/ClinicProvider';
import { formatDateYMD } from '../../utils/dateUtils';
import { styles } from './pharmacyDispanse.styles';
import { ClinicalInvoicePatient, fetchClinicalInvoicePatients } from './pharmacyDispanseHelper';
import TableLoader from '../../components/common/TableLoader';
import NoDataRow from '../../components/common/NoDataRow';
import { useTranslation } from 'react-i18next';


const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const TableHeader: React.FC<{ columns: Array<{ label: string; key: string }> }> = ({ columns }) => (
  <TableHead>
    <TableRow sx={styles.headerRow}>
      {columns.map(col => (
        <TableCell key={col.key} sx={styles.headerCell}>{col.label}</TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const PatientTableRow: React.FC<{ row: PatientRow; onClick: (row: PatientRow) => void; rowAriaLabel: string }> = ({ row, onClick, rowAriaLabel }) => (
  <TableRow
    key={row.id}
    hover
    tabIndex={0}
    sx={styles.interactiveRow}
    onClick={() => onClick(row)}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(row); }}
    aria-label={`${rowAriaLabel} ${row.patient.replace(/\n.*/, '')}`}
  >
    <TableCell sx={styles.rowCell}>{row.date}</TableCell>
    <TableCell sx={{ ...styles.rowCell, whiteSpace: 'pre-line' }}>{row.patient}</TableCell>
    <TableCell sx={{ ...styles.rowCell, whiteSpace: 'pre-line' }}>{row.partner}</TableCell>
    <TableCell sx={styles.rowCell}>{row.contact}</TableCell>
    <TableCell sx={styles.rowCell}>{row.doctor}</TableCell>
  </TableRow>
);

interface PatientRow {
  id: number;
  date: string;
  patient: string;
  partner: string;
  contact: string;
  doctor: string;
}
const transformClinicalPatientToRow = (item: ClinicalInvoicePatient): PatientRow => ({
  id: item.id,
  date: item.appointment_date || '',
  patient: `${item.first_name} ${item.last_name || ''}\n${item.medical_record_no || ''} | ${item.gender__name_label || ''}`.trim(),
  partner: `${item.partner?.first_name || ''} ${item.partner?.last_name || ''}\n${item.partner?.medical_record_no || ''} | ${item.partner?.gender__name_label || ''}`.trim(),
  contact: item.mobile_number || '',
  doctor: item?.treatment_doctor || '',
});

const PharmacyDispense = () => {
  const { t: tBilling } = useTranslation('billing_config');
  // Search & pagination state.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const { ClinicId } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();

  const columns = useMemo(
    () => [
      { label: tBilling('pharmacy_dispense.columns.visit_date', { defaultValue: 'Visit Date' }), key: 'date' },
      { label: tBilling('pharmacy_dispense.columns.patient', { defaultValue: 'Patient' }), key: 'patient' },
      { label: tBilling('pharmacy_dispense.columns.partner', { defaultValue: 'Partner' }), key: 'partner' },
      { label: tBilling('pharmacy_dispense.columns.contact_number', { defaultValue: 'Contact Number' }), key: 'contact' },
      { label: tBilling('pharmacy_dispense.columns.treating_doctor', { defaultValue: 'Treating Doctor' }), key: 'doctor' },
    ],
    [tBilling]
  );

  // Date range state - default to today
  const today = formatDateYMD(new Date());
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // Debounce user input
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(h);
  }, [search]);

  // REPLACE external hook with inline useQuery
  const queryEnabled = !!ClinicId && (debouncedSearch.length === 0 || debouncedSearch.length >= 2);
  const patientsQuery = useQuery({
    queryKey: ['checkedInPatients', ClinicId, page, debouncedSearch, pageSize, fromDate, toDate],
    enabled: queryEnabled,
    staleTime: 30000,
    gcTime: 300000,
    queryFn: () => {
      const offset = pageSize * (page - 1);

      return fetchClinicalInvoicePatients({
        clinic: Number(ClinicId),
        from_date: fromDate,
        to_date: toDate,
        limit: pageSize,
        offset,
        search: debouncedSearch || undefined,
      }).then(res => ({
        rows: res.objects.map(transformClinicalPatientToRow),
        totalCount: res.meta?.total_count || 0,
      }));
    },
  });


  // Stable memoized rows to avoid identity change each render. 
  const patientRows = useMemo(
    () => patientsQuery.data?.rows ?? [],
    [patientsQuery.data?.rows]
  );
  const totalCount = patientsQuery.data?.totalCount || 0;

  const searchingInFlight =
    patientsQuery.isFetching &&
    prevDebouncedSearchRef.current !== debouncedSearch &&
    debouncedSearch.length >= 2;

  const displayedRows = useMemo(() => {
    if (debouncedSearch && patientRows.length > 0) {
      const s = debouncedSearch.toLowerCase();
      return patientRows.filter(r => r.patient.toLowerCase().includes(s));
    }
    return patientRows;
  }, [debouncedSearch, patientRows]);

  const pagination = useMemo(() => {
    const pageCount = Math.ceil(totalCount / pageSize);
    const safePageCount = Math.max(1, pageCount);
    const currentOffset = pageSize * (page - 1);
    const showingStart = totalCount === 0 ? 0 : currentOffset + 1;
    const showingEnd = totalCount === 0 ? 0 : Math.min(currentOffset + patientRows.length, totalCount);
    return { pageCount, safePageCount, showingStart, showingEnd };
  }, [totalCount, page, patientRows.length, pageSize]);

  const storePrevRoute = useCallback(() => {
    sessionStorage.setItem('prevRoute', `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);


  const handleSearchChange = useCallback((_: React.SyntheticEvent, value: string, reason?: string) => {
    if (reason === 'clear') {
      // optional: keep previous search instead of clearing
      // return;
    }
    startTransition(() => {
      setSearch(value);
      setPage(1);
    });
  }, []);

  const handleRowClick = useCallback((row: PatientRow) => {
    storePrevRoute();

    navigate(`/admin/pharmacy-dispense/new/${row.id}`);

  }, [navigate,storePrevRoute]); 

  const getOptionLabel = useCallback((option: string | PatientRow) => {
    if (typeof option === 'string') return option;
    return option.patient ? option.patient.replace(/\n.*/, '') : '';
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, []);

  const tableLoading = patientsQuery.isFetching && prevDebouncedSearchRef.current !== debouncedSearch;

  return (
    <Box>
      <Box>
        <Box sx={styles.headerBar}>
          <Typography variant="h6" sx={styles.title}>
            {tBilling('pharmacy_dispense.patient_list', { defaultValue: 'Patient List' })}
          </Typography>

          {/* Date Range Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mr: 2 }}>
            <TextField
              label={tBilling('pharmacy_dispense.from_date', { defaultValue: 'From Date' })}
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              slotProps={{ 
                inputLabel: { shrink: true },
                htmlInput: { max: toDate }
              }}
              sx={{ width: 160 }}
            />
            <TextField
              label={tBilling('pharmacy_dispense.to_date', { defaultValue: 'To Date' })}
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              slotProps={{ 
                inputLabel: { shrink: true },
                htmlInput: { min: fromDate }
              }}
              sx={{ width: 160 }}
            />
          </Box>

          <Box sx={styles.searchWrap}>
            <Autocomplete
              freeSolo
              disableClearable
              filterOptions={(options) => options}
              options={displayedRows}
              getOptionLabel={getOptionLabel}
              inputValue={search}
              onInputChange={handleSearchChange}
              onClose={() => {
                // Do nothing; prevent Autocomplete from mutating input on blur
              }}
              sx={{ width: '100%' }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tBilling('pharmacy_dispense.search_by_patient', { defaultValue: 'Search by patient' })}
                  size="small"
                  variant="outlined"
                  slotProps={{
                    inputLabel: { shrink: !!search },
                    input: {
                      'aria-label': tBilling('pharmacy_dispense.search_by_patient', { defaultValue: 'Search by patient' }),
                      endAdornment: (
                        <>
                          {search && (
                            <IconButton
                              size="small"
                              aria-label={tBilling('pharmacy_dispense.clear_search', { defaultValue: 'Clear search' })}
                              onClick={handleClearSearch}
                              edge="end"
                              sx={{ p: 0.5, mr: 0.5 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Table */}
        <TableContainer sx={{ ...styles.tableContainer, position: 'relative' }}>
          {tableLoading && <TableLoader minHeight={120} size={32} thickness={3} message={tBilling('pharmacy_dispense.loading', { defaultValue: 'Loading...' })} />}
          <Table size="small" aria-label={tBilling('pharmacy_dispense.table_aria', { defaultValue: 'Pharmacy dispense patient table' })} sx={styles.table}>
            <TableHeader columns={columns} />
            <TableBody>
              {(searchingInFlight ? [] : displayedRows).length > 0
                ? (searchingInFlight ? [] : displayedRows).map(row => (
                  <PatientTableRow key={row.id} row={row} onClick={handleRowClick} rowAriaLabel={tBilling('pharmacy_dispense.create_invoice_for', { defaultValue: 'Create invoice for' })} />
                ))
                : <NoDataRow message={tBilling('pharmacy_dispense.no_patients_found', { defaultValue: 'No patients found with prescription.' })} colSpan={4} />}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Footer: Pagination and entries info */}
        <Box sx={styles.footerBar}>
          <Typography variant="body2" sx={styles.footerText}>
            {tBilling('pharmacy_dispense.showing_entries', {
              defaultValue: 'Showing {{start}} to {{end}} of {{total}} entries',
              start: pagination.showingStart,
              end: pagination.showingEnd,
              total: totalCount,
            })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              select
              size="small"
              label={tBilling('pharmacy_dispense.rows', { defaultValue: 'Rows' })}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1); // Reset to first page when page size changes
              }}
              sx={{ width: 92 }}
              aria-label={tBilling('pharmacy_dispense.rows_per_page', { defaultValue: 'Rows per page' })}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <Pagination
              count={pagination.safePageCount}
              page={page}
              onChange={(_, p) => setPage(p)}
              shape="rounded"
              color="primary"
              size="small"
              disabled={pagination.pageCount <= 1}
              sx={styles.pagination}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
 
export default PharmacyDispense;
